import { useState } from 'react';
import { Copy, Check, ThumbsUp, ThumbsDown } from 'lucide-react';

/**
 * The action row under an assistant answer — copy, 👍/👎, and an optional note on a
 * down-vote. Shared by the internal chat (ChatPanel.jsx) and the public embed widget
 * (EmbedChat.jsx) so a vote looks and behaves identically wherever it is cast.
 *
 * The tool-execution badge is injected as `trace` rather than rendered here: it needs an
 * authenticated traces endpoint the embed surface deliberately has no access to, so the
 * component that owns that access owns the node.
 */
export default function MessageActions({ message, feedback, trace, onVote, onComment }) {
  const [copied, setCopied] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [draft, setDraft] = useState('');
  const rating = feedback?.rating;

  const copy = () => {
    navigator.clipboard.writeText(message.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Clicking the active thumb toggles it off; the other switches. 👎 opens an optional
  // comment box (the down-vote is already recorded on click, comment is extra signal).
  const clickThumb = (r) => {
    if (rating === r) {
      onVote(null);
      setShowComment(false);
      return;
    }
    onVote(r);
    if (r === 'down') {
      setDraft(feedback?.comment || '');
      setShowComment(true);
    } else {
      setShowComment(false);
    }
  };

  const sendComment = () => {
    onComment(draft.trim());
    setShowComment(false);
  };

  const iconButton = 'p-1.5 rounded-lg hover:bg-[var(--app-border)]/40 transition-colors';
  const thumbClass = (active, activeColor) => `${iconButton} ${active ? activeColor : 'text-secondary-500 hover:text-[var(--app-text)]'}`;

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1">
        <button type="button" onClick={copy} title="Copy" aria-label="Copy answer" className={`${iconButton} text-secondary-500 hover:text-[var(--app-text)]`}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
        {trace}
        {message.id && onVote && (
          <>
            <button type="button" onClick={() => clickThumb('up')} title="Good response" aria-label="Good response" aria-pressed={rating === 'up'} className={thumbClass(rating === 'up', 'text-emerald-600')}>
              <ThumbsUp size={14} fill={rating === 'up' ? 'currentColor' : 'none'} />
            </button>
            <button type="button" onClick={() => clickThumb('down')} title="Bad response" aria-label="Bad response" aria-pressed={rating === 'down'} className={thumbClass(rating === 'down', 'text-error-500')}>
              <ThumbsDown size={14} fill={rating === 'down' ? 'currentColor' : 'none'} />
            </button>
          </>
        )}
      </div>
      {showComment && (
        <div className="mt-1 flex items-center gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') sendComment(); }}
            placeholder="What was off? (optional)"
            className="flex-1 text-xs border border-[var(--app-border)] rounded-lg px-2 py-1 bg-[var(--app-surface)] text-[var(--app-text)] min-w-0"
            autoFocus
          />
          <button type="button" onClick={sendComment} className="text-xs px-2 py-1 rounded-lg bg-primary-600 text-white shrink-0">Send</button>
        </div>
      )}
    </div>
  );
}
